// dependencies
import React from "react";
import moment from "moment";
import { Link } from "yarrr";
import PropTypes from "prop-types";
import { List, Map } from "immutable";
import { connect } from "react-redux";

// components
import Button from "Components/Button";
import Avatar from "Components/Avatar";
import Timestamp from "Components/Timestamp";
import ShadowProfile from "../ShadowProfile";
import CustomerControls from "../CustomerControls";
import CSMControls from "../CSMControls";

// selectors
import { getSelectedUser } from "../../selectors";
import { getCurrentEnvironment } from "Selectors/app";

// actions
import { changePage } from "../../actions";

// css
import css from "./EmployeeContainer";

class Employee extends React.Component {
	static propTypes = {
		changePage: PropTypes.func.isRequired,
		userShadowProfile: PropTypes.instanceOf(Map),
		dailyFrequency: PropTypes.array.isRequired,
		dailyFrequencyTimezoneAdjusted: PropTypes.array.isRequired,
		environment: PropTypes.object.isRequired,
		userID: PropTypes.string.isRequired,
		userName: PropTypes.string.isRequired,
		userCompanyName: PropTypes.string.isRequired,
		userCompanyID: PropTypes.string.isRequired,
		userEmail: PropTypes.string.isRequired,
		timezone: PropTypes.string.isRequired,
		userLastSeen: PropTypes.instanceOf(moment),
		deferredLastProcessedAt: PropTypes.instanceOf(Date).isRequired,
		asapLastProcessedAt: PropTypes.instanceOf(Date).isRequired,
		weeklyFrequencyTime: PropTypes.number,
		weeklyFrequencyDay: PropTypes.string,
		adminWeeklyFrequencyTime: PropTypes.number,
		adminWeeklyFrequencyDay: PropTypes.string,
		passwordResetTokens: PropTypes.instanceOf(List),
	};

	static defaultProps = {
		userShadowProfile: Map(),
		userLastSeen: null,
		weeklyFrequencyTime: null,
		weeklyFrequencyDay: null,
		adminWeeklyFrequencyTime: null,
		adminWeeklyFrequencyDay: null,
		passwordResetTokens: List(),
	};

	componentDidMount = () => {};

	changePage = (page) => {
		this.props.changePage(page);
	};

	render = () => {
		const {
			userID,
			timezone,
			userName,
			userEmail,
			userLastSeen,
			userCompanyID,
			userCompanyName,
			userShadowProfile,
			asapLastProcessedAt,
			deferredLastProcessedAt,
			dailyFrequency,
			dailyFrequencyTimezoneAdjusted,
			weeklyFrequencyTime,
			weeklyFrequencyDay,
			adminWeeklyFrequencyTime,
			adminWeeklyFrequencyDay,
			passwordResetTokens,
		} = this.props;

		return (
			<div className={css.main}>
				<div testid="wrapper" className={css.wrapper}>
					<div className={css.userInformation}>
						<div className={css.userCard}>
							<div className={css.avatarWrapper}>
								<Avatar className={css.userAvatar} userID={userID} />
							</div>

							<div className={css.userName}>{userName}</div>
							<Link href={`/companies/${userCompanyID}/`} className={css.userCompany}>
								{userCompanyName}
							</Link>
							<div className={css.userEmail}>{userEmail}</div>
							<div className={css.userEmail}>
								Last Seen: {userLastSeen ? userLastSeen.format("MMM D YYYY h:mm a") : "Never"}
							</div>

							<div className={`${css.separatedContainer}`}>
								<div>
									<div className={css.fieldWrapper}>
										<div className={css.fieldLabel}>Timezone</div>
										<div className={css.fieldValue}>{timezone}</div>
									</div>

									{asapLastProcessedAt ? (
										<div className={css.fieldWrapper}>
											<div className={css.fieldLabel}>Asap Last Processed</div>
											<div className={css.fieldValue}>
												{moment(asapLastProcessedAt).calendar()}
											</div>
										</div>
									) : null}

									{deferredLastProcessedAt ? (
										<div className={css.fieldWrapper}>
											<div className={css.fieldLabel}>Daily Last Processed</div>
											<div className={css.fieldValue}>
												{moment(deferredLastProcessedAt).calendar()}
											</div>
										</div>
									) : null}

									{dailyFrequency.length > 1 ? (
										<div className={css.fieldWrapper}>
											<div className={css.fieldLabel}>
												Expected Daily Notifications in their timezone
											</div>
											<div className={css.fieldValue}>
												{dailyFrequency.map((dd, ii) => {
													if (!dd) {
														return null;
													}

													return (
														<div key={dd} className={css.fieldSubValue}>
															{moment().hour(dd).minute(0).format("h:mm a")} ({" "}
															{dailyFrequencyTimezoneAdjusted[ii]}:00 UTC )
														</div>
													);
												})}
											</div>
										</div>
									) : null}

									{weeklyFrequencyDay ? (
										<div className={css.fieldWrapper}>
											<div className={css.fieldLabel}>
												Expected Weekly Notifications in their timezone
											</div>
											<div className={css.fieldValue}>
												<div className={css.fieldSubValue}>
													{`${
														weeklyFrequencyDay.charAt(0).toUpperCase() +
														weeklyFrequencyDay.slice(1)
													} at ${moment()
														.hour(weeklyFrequencyTime)
														.minute(0)
														.format("h:mm a")}`}
												</div>
											</div>
										</div>
									) : null}

									{adminWeeklyFrequencyDay ? (
										<div className={css.fieldWrapper}>
											<div className={css.fieldLabel}>
												Expected Admin Weekly Notifications in their timezone
											</div>
											<div className={css.fieldValue}>
												<div className={css.fieldSubValue}>
													{`${
														adminWeeklyFrequencyDay.charAt(0).toUpperCase() +
														adminWeeklyFrequencyDay.slice(1)
													} at ${moment()
														.hour(adminWeeklyFrequencyTime)
														.minute(0)
														.format("h:mm a")}`}
												</div>
											</div>
										</div>
									) : null}
								</div>
							</div>

							<div className={`${css.separatedContainer}`}>
								<div className={css.currentTime}>
									<div className={css.currentTimeLabel}>It is currently:</div>
									<div className={css.timezoneWrapper}>
										<Timestamp timezone="UTC" />
									</div>
									<div className={css.timezoneWrapper}>
										<Timestamp timezone={timezone} />
									</div>
								</div>
							</div>

							{userShadowProfile.size > 0 ? (
								<ShadowProfile
									permissionLevel={userShadowProfile.get("permissionLevel")}
									directReportCount={userShadowProfile.get("directReportCount")}
									subjectOfReviewCount={userShadowProfile.get("subjectOfReviewCount")}
									authorOfReviewCount={userShadowProfile.get("authorOfReviewCount")}
									signerOfReviewCount={userShadowProfile.get("signerOfReviewCount")}
									activeAssignedGoalCount={userShadowProfile.get("activeAssignedGoalCount")}
									totalAssignedGoalCount={userShadowProfile.get("totalAssignedGoalCount")}
									feedbackReceivedCount={userShadowProfile.get("feedbackReceivedCount")}
									feedbackSentCount={userShadowProfile.get("feedbackSentCount")}
									completedTodoCount={userShadowProfile.get("completedTodoCount")}
								/>
							) : null}

							{passwordResetTokens.size > 0 ? (
								<div className={`${css.separatedContainer} ${css.tokensContainer}`}>
									<div className={css.tokenTitle}>Password Reset Tokens </div>
									<div>
										{passwordResetTokens.map((token) => {
											const tokenTimestamp = token.get("_id").toString().substring(0, 8);
											const tokenDate = new Date(parseInt(tokenTimestamp, 16) * 1000);
											return (
												<div className={css.passwordToken}>
													<a
														href={`${this.props.environment.host}/set-password/${token.get(
															"token"
														)}/${userID}/`}
														className="newpy grey"
													>
														{tokenDate.toLocaleString("en-US")}
													</a>
												</div>
											);
										})}
									</div>
								</div>
							) : null}

							<div className={css.customerControls}>
								<CustomerControls customerID={userID} />
							</div>
							<div className={css.csmControls}>
								<CSMControls customerID={userID} />
							</div>
							<div className={css.separatedContainer}>
								<div className={css.containerContent}>
									<Button
										href={`${this.props.environment.host}/user/${userID}`}
										external={true}
										className={css.viewInPerformYard}
									>
										View in PerformYard
									</Button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};
}

const mapStateToProps = (state) => {
	const selectedUser = getSelectedUser(state, Map());

	const asapLastProcessedAt = selectedUser.getIn(["notifications", "asapLastProcessedAt"]);
	const deferredLastProcessedAt = selectedUser.getIn(["notifications", "deferredLastProcessedAt"]);

	const dailyFrequency = selectedUser.getIn(["notifications", "dailyFrequency"], List());
	const dailyFrequencyTimezoneAdjusted = selectedUser.getIn(["notifications", "dailyFrequencyInTimezone"], List());

	return {
		userID: selectedUser.get("id"),
		userName: selectedUser.get("name"),
		userEmail: selectedUser.get("email"),
		environment: getCurrentEnvironment(state),
		timezone: selectedUser.get("timezone"),
		userLastSeen: selectedUser.get("lastSeen"),
		userCompanyID: selectedUser.get("companyID"),
		userCompanyName: selectedUser.get("companyName"),
		userShadowProfile: selectedUser.get("shadowProfile"),
		passwordResetTokens: selectedUser.get("passwordResetTokens"),
		dailyFrequencyTimezoneAdjusted: dailyFrequencyTimezoneAdjusted ? dailyFrequencyTimezoneAdjusted.toJS() : [],
		dailyFrequency: dailyFrequency
			? dailyFrequency
					.toJS()
					.map((d) => parseInt(d.hour, 10))
					.sort()
			: [],
		weeklyFrequencyTime: selectedUser.getIn(["notifications", "weeklyFrequency", "hour"]),
		weeklyFrequencyDay: selectedUser.getIn(["notifications", "weeklyFrequency", "day_of_week"]),
		adminWeeklyFrequencyTime: selectedUser.getIn(["notifications", "adminWeeklyFrequency", "hour"]),
		adminWeeklyFrequencyDay: selectedUser.getIn(["notifications", "adminWeeklyFrequency", "day_of_week"]),
		asapLastProcessedAt: asapLastProcessedAt ? moment(asapLastProcessedAt).toDate() : "never",
		deferredLastProcessedAt: deferredLastProcessedAt ? moment(deferredLastProcessedAt).toDate() : "never",
	};
};

const mapDispatchToProps = (dispatch) => ({
	changePage: (pageNumber) => dispatch(changePage(pageNumber)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Employee);
